import React, { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import cloneDeep from 'lodash.clonedeep'
import { useRouter } from 'next/router'

import { usePagination } from '@hooks/usePagination'
import { useAuthentication } from '@hooks/useAuthentication'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { useAlert } from '@hooks/useAlert'
import { BookByUserResponse } from '@models/book/BookByUserResponse'
import { LikeSuccessStatusEnum } from '@models/book/LikeSuccessStatusEnum'
import { BookEnum } from '@interfaces/BookEnum'
import { useTab } from '@components/Tabs/Tabs'
import { scrollToElement } from '@lib/utils'
import { useBookAction } from '@hooks/book/useBookAction'
import { usePenNameAction } from '@hooks/penName/usePenNameAction'
import { ProfileWritingProps, SearchType } from './interface'

export function withProfileWriting(Component: React.FC<ProfileWritingProps>) {
  function WithProfileWriting() {
    const bookClient = useBookAction()
    const router = useRouter()
    const queryClient = useQueryClient()
    const { user, isUserLoading } = useAuthentication()
    const alert = useAlert()
    const storyModal = useModal({ modal: 'createStory' }, true)
    const { page, perpage, pageChange, perpageChange } = usePagination()
    const { handleTabNameChange } = useTab()
    const penNameClient = usePenNameAction()

    const [searchParams, setSearchParams] = useState<SearchType>({
      bookType: null,
      penNameId: null,
    })

    const { data: penNameOptions = [] } = useQuery(
      ['pen-name-options', user?.id],
      () => penNameClient.getPenNameOptions(user?.id),
      { enabled: !!user.id }
    )

    const { data: bookList, isLoading } = useQuery(
      ['my-books', user.id, searchParams, page, perpage],
      () =>
        bookClient.getBooksByUserId({
          ...searchParams,
          id: user.id,
          page,
          limitPerPage: perpage,
        }),
      {
        enabled: !!user.id,
        onSuccess: response => {
          if (response) {
            handleTabNameChange(
              'writing',
              `งานเขียน (${response.totalBook.toLocaleString()})`
            )
          }
        },
      }
    )

    function openCreateStoryModal() {
      storyModal.show()
    }

    function handlePenNameChange(penNameId: number | null) {
      pageChange(1)

      const { bookType, tab } = router.query
      const query: any = {}
      if (bookType) query.bookType = bookType
      if (penNameId) query.penNameId = penNameId
      if (tab) query.tab = tab

      router.replace(
        {
          pathname: router.pathname,
          query,
        },
        undefined,
        { scroll: false }
      )
    }

    function handleBookTypeChange(bookType: BookEnum | null) {
      pageChange(1)

      const { penNameId, tab } = router.query
      const query: any = {}
      if (bookType) query.bookType = bookType
      if (penNameId) query.penNameId = penNameId
      if (tab) query.tab = tab

      router.replace(
        {
          pathname: router.pathname,
          query,
        },
        undefined,
        { scroll: false }
      )
    }

    const { mutate: toggleBookLike, isLoading: isLoadingToggleBookLike } =
      useMutation((id: number) => bookClient.toggleBookLike(id), {
        onSuccess: (type, id) => {
          queryClient.setQueryData<BookByUserResponse | undefined>(
            ['my-books', user.id, searchParams, page, perpage],
            (oldData?: BookByUserResponse) => {
              if (oldData) {
                const dataClone = cloneDeep(oldData)
                const books = dataClone.data
                const index = books.findIndex(item => item.id === id)
                books[index].isLiked = type === LikeSuccessStatusEnum.CREATE
                books[index].likeCount =
                  type === LikeSuccessStatusEnum.CREATE
                    ? books[index].likeCount + 1
                    : Math.max(0, books[index].likeCount - 1)

                return {
                  ...dataClone,
                  data: books,
                }
              }

              return oldData
            }
          )
        },
      })

    async function handleLikeBook(id: number) {
      try {
        await toggleBookLike(id)
      } catch (_) {
        alert.error('เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาลองอีกครั้ง')
      }
    }

    function handlePaginationChange(newPage: number) {
      pageChange(newPage)
      scrollToElement('#my-profile')
    }

    useEffect(() => {
      const bookType = (router.query.bookType as BookEnum) || null
      const penNameId = Number.isNaN(Number(router.query.penNameId))
        ? null
        : Number(router.query.penNameId)

      setSearchParams({
        bookType,
        penNameId,
      })
    }, [router.query.bookType, router.query.penNameId])

    const newProps = {
      data: bookList?.data ?? [],
      penNameOptions,
      searchParams,
      page,
      perpage,
      total: bookList?.total || 0,
      writingDisabled: user.writingDisabled,
      isLoading: isLoading || isUserLoading,
      isLoadingToggleBookLike,
      handlePaginationChange,
      perpageChange,
      openCreateStoryModal,
      handlePenNameChange,
      handleBookTypeChange,
      handleLikeBook,
    }
    return <Component {...newProps} />
  }
  return WithProfileWriting
}
